import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { api } from '@/lib/api';

export default function LaudoModal({ open, onOpenChange, onSuccess, laudo = null }) {
    const { toast } = useToast();
    const [loading, setLoading] = useState(false);
    const [processos, setProcessos] = useState([]);

    const [formData, setFormData] = useState({
        processo_id: '',
        titulo: '',
        versao: '1.0',
        status: 'rascunho',
        arquivo_url: '' // Mock URL logic for now
    });

    useEffect(() => {
        const loadProcessos = async () => {
            const resp = await api.processos.list();
            if (resp.data) {
                setProcessos(resp.data);
            }
        };
        if (open) loadProcessos();
    }, [open]);

    useEffect(() => {
        if (laudo) {
            setFormData({
                processo_id: laudo.processo_id?.toString() || '',
                titulo: laudo.titulo || '',
                versao: laudo.versao || '1.0',
                status: laudo.status || 'rascunho',
                arquivo_url: laudo.arquivo_url || ''
            });
        } else {
            setFormData({
                processo_id: '',
                titulo: '',
                versao: '1.0',
                status: 'rascunho',
                arquivo_url: ''
            });
        }
    }, [laudo, open]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (name, val) => {
        setFormData(prev => ({ ...prev, [name]: val }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.processo_id) {
            toast({ title: "Atenção", description: "Vincule o documento a um processo.", variant: "destructive" });
            return;
        }

        setLoading(true);

        try {
            const payload = {
                processo_id: parseInt(formData.processo_id),
                versao: formData.versao,
                status: formData.status,
                titulo: formData.titulo || `Laudo Pericial v${formData.versao}`,
                arquivo_url: formData.arquivo_url || `/uploads/mock_file_v${formData.versao}.pdf` // Mock URL generator
            };

            let response;
            if (laudo && laudo.id) {
                response = await api.laudos.update(laudo.id, payload);
            } else {
                response = await api.laudos.create(payload);
            }

            if (response && !response.error) {
                toast({
                    title: "Sucesso!",
                    description: laudo ? "Documento atualizado." : "Upload de documento concluído.",
                });
                onSuccess && onSuccess();
                onOpenChange(false);
            } else {
                toast({
                    title: "Erro ao salvar",
                    description: response?.error || "Ocorreu um erro durante a gravação.",
                    variant: "destructive"
                });
            }
        } catch (err) {
            toast({
                title: "Erro de Conexão",
                description: "Falha ao comunicar com o servidor.",
                variant: "destructive"
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>{laudo ? 'Editar Detalhes do Documento' : 'Fazer Upload de Laudo'}</DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4 py-4">
                    <div>
                        <label className="text-sm font-medium mb-1 block text-slate-700">Processo Relacionado *</label>
                        <Select
                            value={formData.processo_id}
                            onValueChange={(val) => handleSelectChange('processo_id', val)}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="Selecione o Processo" />
                            </SelectTrigger>
                            <SelectContent>
                                {processos.map(p => (
                                    <SelectItem key={p.id} value={p.id.toString()}>
                                        {p.numero_cnj || p.numero} - {p.autor || (p.partes ? p.partes.autor : 'Autos')}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div>
                        <label className="text-sm font-medium mb-1 block text-slate-700">Título / Assunto</label>
                        <Input
                            type="text"
                            name="titulo"
                            value={formData.titulo}
                            onChange={handleChange}
                            placeholder="Ex: Laudo Pericial - Engenharia"
                            required
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-medium mb-1 block text-slate-700">Versão</label>
                            <Input
                                type="text"
                                name="versao"
                                value={formData.versao}
                                onChange={handleChange}
                                placeholder="Ex: 1.0"
                                required
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium mb-1 block text-slate-700">Status</label>
                            <Select
                                value={formData.status}
                                onValueChange={(val) => handleSelectChange('status', val)}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Selecione..." />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="rascunho">Rascunho</SelectItem>
                                    <SelectItem value="finalizado">Finalizado / Assinado</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:bg-slate-50 transition-colors cursor-pointer text-slate-500">
                        <div className="mx-auto w-10 h-10 mb-2 rounded-full bg-blue-100 flex items-center justify-center">
                            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path></svg>
                        </div>
                        <p className="text-sm font-medium">Clique ou arraste o arquivo PDF aqui</p>
                        <p className="text-xs text-slate-400 mt-1">(Suporte apenas visual / Mock DB Upload)</p>
                    </div>

                    <DialogFooter className="mt-6">
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            Cancelar
                        </Button>
                        <Button type="submit" disabled={loading} className="bg-[#193D6D] hover:bg-blue-800 text-white">
                            {loading ? 'Salvando...' : (laudo ? 'Atualizar Metadados' : 'Enviar Laudo')}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
